import './rIC.js'

const upgradedElements = new WeakMap()
const DevslabBlocks = new WeakMap()
const parents = new WeakMap()
const children = new WeakMap()
const interactionEvents = new Set(['pointerdown', 'scroll'])

export default class DevslabBlock extends window.HTMLElement {
  constructor() {
    super()
    let setReady
    const isReady = new Promise(resolve => {
      setReady = resolve
    })
    DevslabBlocks.set(this, [isReady, setReady])
  }

  async connectedCallback() {
    if (hasScript(this)) {
      const loadingStrategy = determineLoadingStrategy(this)
      const loadingFunctionWrapper = getLoadingFunctionWrapper(
        loadingStrategy,
        this
      )
      const mediaQuery = getMediaQuery(this)
      const loadingFunction = getLoadingFunction(this)

      if (mediaQuery) {
        await mediaQueryMatches(mediaQuery, this)
      }

      if (hasParent(this) && shouldWaitForParent(this)) {
        const [parentLoaded] = DevslabBlocks.get(parents.get(this))
        await parentLoaded
      }

      if (hasChildren(this) && shouldWaitForChildren(this)) {
        const childComponents = children.get(this)
        for (const child of childComponents) {
          const [childLoaded] = DevslabBlocks.get(child)
          await childLoaded
        }
      }

      loadingFunctionWrapper(loadingFunction)
    } else {
      setComponentReady(this)
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect()
    this.mediaQueryList?.removeEventListener('change')
    cleanupElement(this)
  }
}

function getComponentPath(node) {
  return node.getAttribute('name')
}

function hasScript(node) {
  const componentName = getComponentPath(node)
  if (!componentName) return false
  return !!window.DevslabData.blocksWithScript[componentName]
}

function getScriptImport(node) {
  const componentPath = getComponentPath(node)
  if (!componentPath) return null

  const baseUrl = window.DevslabData?.templateDirectoryUri || ''
  const version = window.DevslabData?.version || '1.0.0'
  const scriptPath = `${baseUrl}/dist/public/components/block/${componentPath}/script.min.js?v=${version}`

  return async () => {
    const module = await import(/* webpackIgnore: true */ scriptPath)
    if (module.__esModule) {
      return module.default
    }
    return module
  }
}

function hasParent(node) {
  if (!parents.has(node)) {
    const parent = node.parentElement.closest('devslab-component')
    parents.set(node, parent)
    return !!parent
  }
  return !!parents.get(node)
}

function hasChildren(node) {
  if (!children.has(node)) {
    const childComponents = node.querySelectorAll('devslab-component')
    children.set(node, childComponents)
    return childComponents.length > 0
  }
  return children.get(node).length > 0
}

function shouldWaitForParent(node) {
  return node.getAttribute('data-wait') === 'parent'
}

function shouldWaitForChildren(node) {
  return node.getAttribute('data-wait') === 'children'
}

function setComponentReady(node) {
  const setReady = DevslabBlocks.get(node)[1]
  setReady()
}

function visible(node) {
  return new Promise(function (resolve) {
    const observer = new IntersectionObserver(function (entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          observer.disconnect()
          resolve(true)
        }
      }
    })
    observer.observe(node)
    node.observer = observer
  })
}

function mediaQueryMatches(query, node) {
  return new Promise(function (resolve) {
    const mediaQueryList = window.matchMedia(query)
    if (mediaQueryList.matches) {
      resolve(true)
    } else {
      mediaQueryList.addEventListener('change', () => resolve(true), {
        once: true,
      })
    }
    node.mediaQueryList = mediaQueryList
  })
}

function determineLoadingStrategy(node) {
  const defaultStrategy = 'load'
  const strategies = {
    load: 'load',
    idle: 'idle',
    visible: 'visible',
    interaction: 'interaction',
  }
  return strategies[node.getAttribute('load:on')] ?? defaultStrategy
}

function getLoadingFunctionWrapper(strategyName, node) {
  const loadingFunctions = {
    load: x => x(),
    idle: x => requestIdleCallback(x, { timeout: 2000 }),
    visible: async x => {
      await visible(node)
      x()
    },
    interaction: x => {
      const load = () => {
        interactionEvents.forEach(event =>
          document.removeEventListener(event, load)
        )
        x()
      }
      interactionEvents.forEach(event =>
        document.addEventListener(event, load, { once: true })
      )
    },
  }
  return loadingFunctions[strategyName] ?? loadingFunctions.load
}

function getMediaQuery(node) {
  return node.hasAttribute('load:on:media')
    ? node.getAttribute('load:on:media')
    : null
}

function getLoadingFunction(node) {
  return async () => {
    const componentScriptImport = getScriptImport(node)
    if (!componentScriptImport) {
      setComponentReady(node)
      return
    }

    try {
      const importedScript = await componentScriptImport()

      if (typeof importedScript.default === 'function') {
        const cleanupFn = importedScript.default(node)
        upgradedElements.set(node, cleanupFn)
      } else {
        console.log('No valid function found in component script')
        console.log('Full component script:', importedScript)
      }
    } catch (e) {
      console.error(
        `Error loading script for component: ${getComponentPath(node)}`,
        e
      )
    }
    setComponentReady(node)
  }
}

function cleanupElement(node) {
  if (upgradedElements.has(node)) {
    const cleanupFn = upgradedElements.get(node)
    if (typeof cleanupFn === 'function') {
      cleanupFn(node)
    }
    upgradedElements.delete(node)
  }
}
